/**
 * WHERE a session came from, and — since wave 10 (D21) — what it may open.
 *
 * THE PROVIDER HALF is unchanged: the source of truth is `/auth/me` (T.Y16, the
 * `user.authProvider` field, contract spelling `YANDEX`/`EMAIL_CODE`), with the
 * per-tab sessionStorage marker as a FALLBACK for deploy skew and the moment
 * between the code verify and the `/auth/me` refetch. The login page keeps
 * writing it, the logout keeps clearing it.
 *
 * THE BOUNDARY HALF changed in wave 10: the provider no longer decides anything.
 * The api's fifth guard gates the staff/cabinet fence on the account's ROLE and
 * its venue LINKS (D21 — every account may sign in both ways, so a
 * provider-based fence would answer the same person differently depending on
 * the door). The nav reads the same signals the api enforces, through
 * {@link canSeeCabinet} and {@link isCuratorRole}:
 *
 *   * КУРАТОР (the `VIEWER` enum value) → «Кабинет», never the staff screens;
 *   * МЕНЕДЖЕР → the staff screens, plus «Кабинет» only when the api's effective
 *     cabinet scope is non-empty (a prefill-capable manager: a flagged linked venue);
 *   * АДМИН → the staff screens including the ADMIN tier, and — A6 (owner UX pass,
 *     2026-09-12) — NOT «Кабинет»;
 *   * ПОДДЕРЖКА (`SUPPORT`, T.Z157) → the staff screens wherever an АДМИН has them —
 *     the T.Z155 rank already opens every `minRole` gate for it, so nothing here may
 *     re-classify the role as a curator (the live bug T.Z157 fixes came from exactly
 *     that classification on the api side).
 *
 * T.Z60 (owner, 2026-09-15) adds the curator's own page and order: «Группы»
 * ({@link canSeeGroups}) is a CURATOR surface — the nav never offers it to staff —
 * and the curator's three items read «Кабинет», «Педагоги», «Группы»
 * ({@link curatorNavOrder}), not the staff array's order.
 *
 * T.Z85 (owner, 2026-09-16) gives the role the FIRST word too: where a fresh
 * session LANDS ({@link landingAfterLogin}) is decided by the same
 * {@link isCuratorRole} predicate — a КУРАТОР-role session opens «Кабинет», a
 * staff session the dashboard — whichever door it came in through. Until this
 * task the landing was the login page's private decision, made by PROVIDER (the
 * code door → `/cabinet`, the Yandex door → `/`), which is the wave-9 answer D21
 * retired: a Yandex КУРАТОР hit the staff dashboard and read its 403
 * `CURATOR_SCOPE_REQUIRED` as «Что-то пошло не так».
 *
 * Pure on purpose: the storage is a parameter, so the `unit` project runs the whole
 * file in plain Node.
 */

/** The contract's `AuthProvider` (v1.8.0), as the SPA spells it. */
export type AuthProvider = 'YANDEX' | 'EMAIL_CODE';

/** The session-level provider spelling the api uses internally (`email_code`). */
export type SessionProvider = 'email_code' | 'yandex';

export const AUTH_PROVIDER_STORAGE_KEY = 'cbm.authProvider';

/** The narrow `Storage` surface the marker needs (a `Storage` or a test double). */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * FALLBACK ONLY (T.Y16) — reads the verify-time marker the login page wrote. The real
 * source is the `authProvider` field on `/auth/me`; the marker covers deploy skew and
 * the moment before that answer is refetched. Anything but the two spellings reads as
 * absent.
 */
export function readProviderMarker(storage: KeyValueStorage | null | undefined): AuthProvider | null {
  if (!storage) return null;
  const value = storage.getItem(AUTH_PROVIDER_STORAGE_KEY);
  return value === 'YANDEX' || value === 'EMAIL_CODE' ? value : null;
}

/** Called by the login page the moment #2.6 answers 200 (feeding the fallback above). */
export function writeProviderMarker(storage: KeyValueStorage | null | undefined, provider: AuthProvider): void {
  storage?.setItem(AUTH_PROVIDER_STORAGE_KEY, provider);
}

/** Called on the way out (logout interstitial, login screen mount). */
export function clearProviderMarker(storage: KeyValueStorage | null | undefined): void {
  storage?.removeItem(AUTH_PROVIDER_STORAGE_KEY);
}

/**
 * Normalises the `/auth/me` field (T.Y16). Both spellings are accepted — the
 * contract's `YANDEX`/`EMAIL_CODE` and the api's session spelling
 * `yandex`/`email_code` — because a field we do not control the case of must not
 * read as absent.
 */
export function parseAuthProvider(value: unknown): AuthProvider | null {
  if (typeof value !== 'string') return null;
  const candidate = value.trim().toUpperCase().replace('_', '-');
  if (candidate === 'EMAIL-CODE') return 'EMAIL_CODE';
  if (candidate === 'YANDEX') return 'YANDEX';
  return null;
}

/**
 * The session provider as the UI sees it: the `/auth/me` field FIRST (T.Y16), the
 * sessionStorage marker only when the field is absent, and `AUTH_DISABLED` as its own
 * staff-shaped answer. `null` = an unprovable session.
 *
 * WAVE 10: this is a DESCRIPTION of the login hop now — nothing gates on it. The
 * api's fifth guard enforces the role+link boundary, and the nav mirrors it through
 * {@link canSeeCabinet} / {@link isCuratorRole}; a stale marker can therefore never
 * open OR close a surface the api has already decided about.
 */
export function resolveSessionProvider(input: {
  authDisabled?: boolean;
  meAuthProvider?: unknown;
  marker?: AuthProvider | null;
}): SessionProvider | null {
  const fromApi = parseAuthProvider(input.meAuthProvider);
  if (fromApi === 'EMAIL_CODE') return 'email_code';
  if (fromApi === 'YANDEX') return 'yandex';
  // FALLBACK ONLY from here down: the api did not publish a usable field.
  if (input.marker === 'EMAIL_CODE') return 'email_code';
  if (input.authDisabled === true) return 'yandex';
  if (input.marker === 'YANDEX') return 'yandex';
  return null;
}

/** True when the role parses to ADMIN (`hasAtLeast` without the role-module import). */
function isAdmin(role: unknown): boolean {
  return typeof role === 'string' && role.trim().toUpperCase() === 'ADMIN';
}

/**
 * The SUPPORT role (T.Z155/T.Z157), spelled the same careful way as {@link isAdmin}:
 * a string comparison rather than the rank, because these predicates answer "which
 * SURFACE is yours" — a question the rank deliberately does not settle (SUPPORT outranks
 * ADMIN and must never read as a curator because of it). Exported for the nav rules
 * below and their spec, never for a gate.
 */
export function isSupportRole(role: unknown): boolean {
  return typeof role === 'string' && role.trim().toUpperCase() === 'SUPPORT';
}

/**
 * The КУРАТОР role, as the api means it: the `VIEWER` enum value. One role per user
 * (D21) — a VIEWER holds no staff surface at all, whichever way they signed in, so
 * the nav offers them the cabinet INSTEAD of the staff list.
 */
export function isCuratorRole(role: unknown): boolean {
  return typeof role === 'string' && role.trim().toUpperCase() === 'VIEWER';
}

export interface CabinetVisibilityInput {
  /** The session role as `/auth/me` published it — absent/unparseable reads fail-closed. */
  role?: unknown;
  /**
   * The api's EFFECTIVE cabinet scope (`user.cabinetVenueIds` on `/auth/me`, wave 10):
   * the venues #26–#26.6 answer for THIS session. Absent (an older api pod, deploy
   * skew) degrades to "unknown" — a КУРАТОР still sees the item, a МЕНЕДЖЕР does not.
   */
  cabinetVenueIds?: ReadonlyArray<number> | null;
  /** The curated links, as a fallback when the effective scope was not published. */
  curatorVenueIds?: ReadonlyArray<number> | null;
}

/**
 * Who is OFFERED «Кабинет» (wave 10, D21 — role + links, A6 for the admin half):
 *
 *   * a КУРАТОР-role session — the cabinet is what they get INSTEAD of the staff
 *     screens, and their curator links (even link-less, the page itself answers 403)
 *     are the api's business, not the nav's;
 *   * a МЕНЕДЖЕР with a NON-EMPTY effective cabinet scope — the prefill-capable
 *     manager (M1): they prefill children on their flagged linked venues;
 *   * never an ADMIN (A6): the admin works from the staff screens;
 *   * never SUPPORT (T.Z157): the role is ADMIN-level ACCESS (its api cabinet scope is
 *     always the empty set — no link may hang on a SUPPORT account), so it works from
 *     the staff screens exactly where an ADMIN does.
 */
export function canSeeCabinet(input: CabinetVisibilityInput): boolean {
  if (isAdmin(input.role)) return false;
  if (isSupportRole(input.role)) return false;
  if (isCuratorRole(input.role)) return true;
  // A МЕНЕДЖЕР (or an unparseable role — fail closed): only a published, non-empty
  // cabinet scope opens the item.
  return Array.isArray(input.cabinetVenueIds) && input.cabinetVenueIds.length > 0;
}

/**
 * Who is OFFERED «Группы» (T.Z60, owner 2026-09-15): a КУРАТОР-role session ONLY.
 * The owner's words were ««Группы» is a CURATOR surface; staff never sees it», so a
 * prefill-capable МЕНЕДЖЕР keeps the cabinet WITHOUT this item — their groups stay
 * reachable through the cabinet scope on the wire (#26.8–#26.13 answer any session
 * whose cabinet scope holds the venue), the nav just does not advertise the page.
 */
export function canSeeGroups(input: CabinetVisibilityInput): boolean {
  return isCuratorRole(input.role);
}

/** The staff start page (the dashboard) — the landing for every other role. */
export const STAFF_LANDING_PATH = '/';

/** The curator start page — where a КУРАТОР-role session belongs. */
export const CURATOR_LANDING_PATH = '/cabinet';

/**
 * T.Z85 (owner, 2026-09-16) — the start page of a fresh session, BY ROLE.
 *
 * A КУРАТОР-role session ({@link isCuratorRole} — the same predicate the nav and
 * the api's fifth guard speak) lands on its cabinet; a staff session lands on the
 * dashboard. NOTHING else is consulted: not the provider (D21 — the door changed
 * nothing since wave 10), not the cabinet scope — a scope-less КУРАТОР lands on
 * `/cabinet` too and reads the page's own honest 403 `CURATOR_SCOPE_REQUIRED`,
 * which is the arrangement the api already renders there. An absent/unparseable
 * role reads as staff (the dashboard): the api is the boundary, and `/cabinet`
 * is a page this session was never offered.
 *
 * SUPPORT (T.Z157) is the exact case the VIEWER-only predicate gets right: the role
 * is NOT a curator, so it lands on the dashboard — the staff default — never on the
 * cabinet, whichever door it signed in through.
 *
 * Consumers: the post-verify hop of the login page (`landingAfterVerify`, the
 * wire half of #2.6) and the `/` index route (a hand-typed or bookmarked `/`,
 * plus the api's OAuth callback that redirects EVERYONE to `/`). Both call this
 * one function — a second landing definition is exactly what T.Z85 deletes.
 */
export function landingAfterLogin(role: unknown): typeof CURATOR_LANDING_PATH | typeof STAFF_LANDING_PATH {
  return isCuratorRole(role) ? CURATOR_LANDING_PATH : STAFF_LANDING_PATH;
}

/**
 * The effective cabinet scope of a session, as `/auth/me` publishes it (wave 10):
 * `cabinetVenueIds` when the api sent it, else the curated links (the КУРАТОР shape,
 * which was the only cabinet there was before wave 10). Empty/absent = nothing.
 */
export function cabinetScopeOf(input: { cabinetVenueIds?: ReadonlyArray<number> | null; curatorVenueIds?: ReadonlyArray<number> | null }): ReadonlyArray<number> {
  if (Array.isArray(input.cabinetVenueIds)) return input.cabinetVenueIds;
  if (Array.isArray(input.curatorVenueIds)) return input.curatorVenueIds;
  return [];
}

/**
 * T.Z60 (owner, 2026-09-15) — the КУРАТОР's list is its OWN sequence: «Кабинет»,
 * then «Педагоги», then «Группы»; «Дашборд» (DATAANALYTICS-112, owner 03.11) goes directly below «Кабинет». The staff array keeps its own order (the owner
 * moved nothing there), so the sequence cannot be expressed by re-ordering the one
 * NAV_ITEMS list the staff nav reads — it is stated here instead, by id.
 */
export const CURATOR_NAV_IDS: readonly string[] = ['cabinet', 'my-dashboard', 'teachers', 'groups'];

/**
 * Puts a filtered nav list into {@link CURATOR_NAV_IDS}' order. Ids the list does not
 * carry (a hidden item) drop out; ids outside the sequence keep their relative order
 * after the sequenced ones, so a future curator-visible item stays visible even if
 * the sequence above forgets it.
 */
export function curatorNavOrder<T extends { id: string }>(items: readonly T[]): T[] {
  const sequenced = CURATOR_NAV_IDS.flatMap((id) => {
    const found = items.find((item) => item.id === id);
    return found === undefined ? [] : [found];
  });
  const rest = items.filter((item) => !CURATOR_NAV_IDS.includes(item.id));
  return [...sequenced, ...rest];
}
